/**
 * Orbit region suggestion (replaces the Orbe app). Markup: snippets/orbit-geo.liquid.
 *
 * Asks Shopify where the visitor is (/browsing_context_suggestions.json, the endpoint
 * Shopify's own Geolocation app uses) and, when that country or the browser's language
 * differs from the store being shown, opens the region panel with country, language and
 * currency already set to match. Continue posts Shopify's localization form (Shopify moves
 * the shopper to the right market subfolder) and saves the currency; Cancel or close hides
 * it for `dismissDays`. A choice is remembered for `rememberDays`, and a choice made in the
 * header's region menu counts too.
 *
 * Never runs for bots, in the theme editor, on the cart/checkout/account pages, or when the
 * visitor arrived from a brand landing page (?brand= / ?market=: that link already chose).
 * QA: ?orbit_geo=force&orbit_geo_country=AU opens it whatever the cookies and settings say.
 *
 * Also exposes window.orbit.geo.detect() (the detected country code, shared promise) for
 * assets/orbit-currency.js, and window.orbit.geo.open() to open the panel on demand.
 */
const data = readJSON('OrbitLocaleData');
const params = new URLSearchParams(location.search);
const force = params.get('orbit_geo') === 'force';
const fakeCountry = (params.get('orbit_geo_country') || '').toUpperCase();
const COOKIE = 'orbit_geo';

function readJSON(id) {
  try {
    return JSON.parse(document.getElementById(id)?.textContent || 'null');
  } catch {
    return null;
  }
}

function readCookie(name) {
  const match = document.cookie.match(`(?:^|; )${name}=([^;]*)`);
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(value, days) {
  document.cookie = `${COOKIE}=${encodeURIComponent(value)};path=/;max-age=${Math.round(days * 86400)};SameSite=Lax;Secure`;
}

/* ── Detection ─────────────────────────────────────────────────────────── */

let detection = null;
function detect() {
  if (detection) return detection;
  detection = (async () => {
    if (/^[A-Z]{2}$/.test(fakeCountry)) return fakeCountry;
    try {
      const cached = JSON.parse(sessionStorage.getItem('orbit_geo_country') || 'null');
      if (cached?.code) return cached.code;
    } catch {}
    try {
      const root = data?.root || '/';
      const url = `${root.replace(/\/$/, '')}/browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true`;
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      const json = await response.json();
      const code = json?.detected_values?.country?.handle || null;
      if (code) {
        try {
          sessionStorage.setItem('orbit_geo_country', JSON.stringify({ code, ts: Date.now() }));
        } catch {}
      }
      return code;
    } catch {
      return null;
    }
  })();
  return detection;
}

/* Every market sells in BRL, so Shopify reports BRL as every country's currency; the
   visitor's own currency comes from this table (the countries the markets cover). */
const COUNTRY_CURRENCY = Object.fromEntries(
  'AE:AED AR:ARS AT:EUR AU:AUD BE:EUR BG:EUR BO:BOB BR:BRL CA:CAD CH:CHF CL:CLP CO:COP CR:CRC CY:EUR CZ:CZK DE:EUR DK:DKK DO:DOP EC:USD EE:EUR ES:EUR FI:EUR FR:EUR GB:GBP GR:EUR GT:GTQ HK:HKD HN:HNL HR:EUR HU:HUF IE:EUR IL:ILS IN:INR IT:EUR JP:JPY KR:KRW LT:EUR LU:EUR LV:EUR MT:EUR MX:MXN MY:MYR NI:NIO NL:EUR NO:NOK NZ:NZD PA:USD PE:PEN PL:PLN PT:EUR PY:PYG RO:RON SA:SAR SE:SEK SG:SGD SI:EUR SK:EUR SV:USD TW:TWD US:USD UY:UYU VE:USD ZA:ZAR'.split(' ').map((pair) => pair.split(':'))
);
const currencyOf = (country) => COUNTRY_CURRENCY[country] || null;

window.orbit = window.orbit || {};
window.orbit.geo = { detect, currencyOf, open: () => openPanel(null, { manual: true }) };

/* ── Language ──────────────────────────────────────────────────────────── */

/** Published language codes by primary subtag: { pt: 'pt-BR', en: 'en', es: 'es' }. */
function languagesByPrimary() {
  const byPrimary = {};
  for (const code of Object.keys(data?.languages || {})) byPrimary[code.toLowerCase().split('-')[0]] = code;
  return byPrimary;
}

/* The store publishes Portuguese, English and Spanish: a country gets its own language when
   it is one of those, English otherwise. Shopify lists only the current market's languages;
   every market publishes Spanish today, and should one stop, the panel still offers it for
   Spanish-speaking countries (Shopify then falls back to that market's default). */
const SPANISH = new Set('AR BO CL CO CR CU DO EC ES GT HN MX NI PA PE PR PY SV UY VE'.split(' '));
const PORTUGUESE = new Set('AO BR CV GW MZ PT ST TL'.split(' '));
const OFFERED = { es: ['es', 'Español'] };
function languageOf(country) {
  const byPrimary = languagesByPrimary();
  const primary = SPANISH.has(country) ? 'es' : PORTUGUESE.has(country) ? 'pt' : 'en';
  if (byPrimary[primary]) return byPrimary[primary];
  const offered = OFFERED[primary];
  if (offered && languageSelect) {
    if (!hasOption(languageSelect, offered[0])) languageSelect.add(new Option(offered[1], offered[0]));
    return offered[0];
  }
  return byPrimary.en || data?.current?.language;
}

function preferredLanguage() {
  const byPrimary = languagesByPrimary();
  for (const tag of navigator.languages || [navigator.language]) {
    const primary = String(tag).toLowerCase().split('-')[0];
    if (byPrimary[primary]) return byPrimary[primary];
  }
  return data?.current?.language;
}

/* ── Panel ─────────────────────────────────────────────────────────────── */

const dialog = document.querySelector('[data-orbit-geo]');
const form = document.getElementById('OrbitGeoForm');
const countrySelect = dialog?.querySelector('[data-orbit-geo-country]');
const languageSelect = dialog?.querySelector('[data-orbit-geo-language]');
const currencySelect = dialog?.querySelector('[data-orbit-geo-currency]');
const currencyField = dialog?.querySelector('[data-orbit-geo-currency-field]');
const flag = dialog?.querySelector('[data-orbit-geo-flag]');
const suggestion = dialog?.querySelector('[data-orbit-geo-suggestion]');
let currencyTouched = false;
let languageTouched = false;
let manualOpen = false;

function hasOption(select, value) {
  return Boolean(select && [...select.options].some((option) => option.value === value));
}

function localCurrency(country) {
  const code = currencyOf(country);
  if (!code || !currencySelect) return 'BRL';
  if (!hasOption(currencySelect, code)) currencySelect.add(new Option(code, code));
  return code;
}

function syncCountry() {
  const country = countrySelect.value;
  const entry = data?.countries?.[country];
  if (flag && entry?.[2]) flag.src = entry[2];
  if (currencySelect) delete currencySelect.dataset.orbitLabelled;
  if (currencySelect && !currencyTouched) currencySelect.value = localCurrency(country);
}

/* A country picked in the panel brings its language and currency, unless the shopper has
   already picked either one in this panel. */
function changeCountry() {
  if (languageSelect && !languageTouched) {
    const language = languageOf(countrySelect.value);
    if (hasOption(languageSelect, language)) languageSelect.value = language;
  }
  syncCountry();
}

/**
 * Opens the panel. A suggestion (`detected` set) preselects the visitor's own country,
 * language and currency; a manual open (the globe, the header) shows the current choices.
 */
function openPanel(detected, { manual = false } = {}) {
  if (!dialog || dialog.open) return;
  manualOpen = manual;
  const current = data.current;
  const country = detected && data.countries?.[detected] ? detected : current.country;
  countrySelect.value = country;
  if (languageSelect) {
    const language = manual ? current.language : preferredLanguage();
    if (hasOption(languageSelect, language)) languageSelect.value = language;
  }
  if (currencyField) currencyField.hidden = !(data.currency?.enabled || params.get('orbit_currency'));
  // Only a language or currency picked in this panel sticks; until then a new country
  // brings its own.
  currencyTouched = false;
  languageTouched = false;
  const active = window.orbit?.currency?.code;
  if (currencySelect && manual && active) {
    // Opened by hand: start from the currency on screen, then follow any country change.
    if (!hasOption(currencySelect, active)) currencySelect.add(new Option(active, active));
    currencySelect.value = active;
    if (flag && data.countries?.[country]?.[2]) flag.src = data.countries[country][2];
  } else {
    if (currencySelect) currencySelect.value = active && active !== 'BRL' ? active : localCurrency(country);
    syncCountry();
  }
  if (suggestion) {
    const name = detected && data.countries?.[detected]?.[0];
    suggestion.textContent = name ? data.t.suggestion.replace('[country]', name) : '';
  }
  dialog.showModal();
  dialog.querySelector('[data-orbit-geo-continue]')?.focus();
  if (window.gtag) window.gtag('event', 'orbit_geo', { action: manual ? 'opened' : 'shown', country: detected || current.country });
}

function dismiss() {
  // Closing a panel the shopper opened is not a "no" to a suggestion.
  if (!manualOpen) writeCookie(`dismissed:${countrySelect?.value || ''}`, data.geo?.dismissDays || 7);
  if (window.gtag) window.gtag('event', 'orbit_geo', { action: 'dismiss' });
  dialog.close();
}

async function confirm() {
  const country = countrySelect.value;
  const language = languageSelect?.value || data.current.language;
  const currency = currencySelect && !currencyField?.hidden ? currencySelect.value : null;
  writeCookie(`chosen:${country}:${language}`, data.geo?.rememberDays || 60);
  if (window.gtag) window.gtag('event', 'orbit_geo', { action: 'accept', country, language, currency: currency || '' });
  if (currency) {
    try {
      localStorage.setItem('orbit_currency', JSON.stringify({ code: currency, mode: 'manual', ts: Date.now() }));
    } catch {}
  }
  if (country !== data.current.country || language !== data.current.language) {
    form.elements.country_code.value = country;
    form.elements.language_code.value = language;
    form.submit();
    return;
  }
  dialog.close();
  if (currency) await window.orbit?.setCurrency?.(currency, 'manual');
}

if (dialog) {
  countrySelect?.addEventListener('change', changeCountry);
  languageSelect?.addEventListener('change', () => (languageTouched = true));
  currencySelect?.addEventListener('change', () => (currencyTouched = true));
  dialog.querySelector('[data-orbit-geo-continue]')?.addEventListener('click', confirm);
  for (const button of dialog.querySelectorAll('[data-orbit-geo-cancel]')) button.addEventListener('click', dismiss);
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    dismiss();
  });
  // A click on the dimmed page outside the panel closes it, like Cancel.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dismiss();
  });
}

/* The floating globe is the region entry point; revealed only once it can open the panel. */
const globe = document.querySelector('[data-orbit-globe]');
if (globe && dialog && data) {
  globe.hidden = false;
  globe.addEventListener('click', () => openPanel(null, { manual: true }));
}

/* The header and menu-drawer region buttons (if the header still shows them) open this panel instead of Horizon's dropdown
   (capture phase, so their own handlers never run). Without the panel, or if this script
   fails, the dropdown keeps working as before. */
const REGION_TRIGGERS = 'dropdown-localization-component > button[ref="button"], #HeaderDrawer-localization';
if (dialog && data) {
  for (const trigger of document.querySelectorAll(REGION_TRIGGERS)) trigger.setAttribute('aria-haspopup', 'dialog');
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest?.(REGION_TRIGGERS);
      if (!trigger) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openPanel(null, { manual: true });
    },
    true
  );
}

/* A region chosen in the header menu is a choice too: stop suggesting. */
document.addEventListener('click', (event) => {
  const item = event.target.closest?.('localization-form-component .localization-form__list-item[data-value]');
  if (item && data) writeCookie(`chosen:${item.dataset.value}:${data.current.language}`, data.geo?.rememberDays || 60);
});
document.addEventListener('change', (event) => {
  if (event.target.matches?.('localization-form-component select[name="language_code"]') && data) {
    writeCookie(`chosen:${data.current.country}:${event.target.value}`, data.geo?.rememberDays || 60);
  }
});

/* ── When to suggest ───────────────────────────────────────────────────── */

function blocked() {
  if (!data || !dialog || !form) return true;
  if (force) return false;
  if ((data.geo?.mode || 'off') === 'off') return true;
  if (data.designMode || navigator.webdriver || document.prerendering) return true;
  if (/bot|crawl|spider|slurp|lighthouse|headless|preview|facebookexternalhit|whatsapp|telegram|pingdom/i.test(navigator.userAgent)) return true;
  if (params.has('brand') || params.has('market') || params.get('orbit_geo') === 'off') return true;
  if (readCookie(COOKIE)) return true;
  const path = location.pathname.replace(/^\/[a-z]{2}(-[a-z0-9]+)?(?=\/)/i, '');
  if (/^\/(cart|checkouts?|account|password|challenge)/.test(path)) return true;
  return false;
}

async function run() {
  if (blocked()) return;
  const detected = await detect();
  const current = data.current;
  const countryDiffers = detected && detected !== current.country && data.countries?.[detected];
  const language = preferredLanguage();
  const languageDiffers = language && language !== current.language;
  if (!force && !countryDiffers && !languageDiffers) return;

  if (!force && data.geo.mode === 'auto' && countryDiffers) {
    const navigation = performance.getEntriesByType?.('navigation')?.[0];
    if (document.visibilityState === 'visible' && (!navigation || navigation.type === 'navigate')) {
      writeCookie(`auto:${detected}:${language}`, data.geo?.rememberDays || 60);
      form.elements.country_code.value = detected;
      form.elements.language_code.value = language || current.language;
      form.submit();
      return;
    }
  }
  openPanel(detected);
}

const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 300));
if (document.readyState === 'complete') idle(run);
else addEventListener('load', () => idle(run), { once: true });
